import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Channel, Video } from '@meradio/shared-types';
import * as youtubeApi from '@/services/youtube.api';
import * as channelsApi from '@/services/channels.api';

export interface ChannelsState {
  channels: Channel[];
  followedChannels: Channel[];
  searchResults: Channel[];
  selectedChannel: Channel | null;

  isLoading: boolean;
  isSearching: boolean;
  isFollowing: boolean;
  isUnfollowing: boolean;
  isImporting: boolean;

  error: string | null;
  searchError: string | null;
  followError: string | null;

  searchQuery: string;
  hasSearched: boolean;

  searchHasMore: boolean;
  followedHasMore: boolean;
}

const initialState: ChannelsState = {
  channels: [],
  followedChannels: [],
  searchResults: [],
  selectedChannel: null,
  isLoading: false,
  isSearching: false,
  isFollowing: false,
  isUnfollowing: false,
  isImporting: false,
  error: null,
  searchError: null,
  followError: null,
  searchQuery: '',
  hasSearched: false,
  searchHasMore: true,
  followedHasMore: true,
};

export const searchChannels = createAsyncThunk(
  'channels/searchChannels',
  async ({ query, maxResults = 25 }: { query: string; maxResults?: number }) => {
    const channels = await youtubeApi.searchChannels(query, maxResults);
    return { channels, query };
  }
);

export const getFollowedChannels = createAsyncThunk(
  'channels/getFollowedChannels',
  async () => {
    const channels = await channelsApi.getFollowedChannels();
    return channels;
  }
);

export const followChannel = createAsyncThunk(
  'channels/followChannel',
  async (channel: Channel, { rejectWithValue }) => {
    try {
      await channelsApi.followChannel(channel.channelId);
      return channel;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to follow channel');
    }
  }
);

export const unfollowChannel = createAsyncThunk(
  'channels/unfollowChannel',
  async (channelId: string, { rejectWithValue }) => {
    try {
      await channelsApi.unfollowChannel(channelId);
      return channelId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to unfollow channel');
    }
  }
);

export const getChannelMetadata = createAsyncThunk(
  'channels/getChannelMetadata',
  async (channelId: string) => {
    const channel = await youtubeApi.getChannelMetadata(channelId);
    return channel;
  }
);

export const triggerManualImport = createAsyncThunk(
  'channels/triggerManualImport',
  async (channelId: string, { rejectWithValue }) => {
    try {
      await youtubeApi.triggerManualImport(channelId);
      return channelId;
    } catch (error: any) {
      return rejectWithValue(youtubeApi.parseYouTubeError(error));
    }
  }
);

const channelsSlice = createSlice({
  name: 'channels',
  initialState,
  reducers: {
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchQuery = '';
      state.hasSearched = false;
      state.searchError = null;
    },

    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },

    clearErrors: (state) => {
      state.error = null;
      state.searchError = null;
      state.followError = null;
    },

    selectChannel: (state, action: PayloadAction<Channel | null>) => {
      state.selectedChannel = action.payload;
    },

    resetChannelsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchChannels.pending, (state) => {
        state.isSearching = true;
        state.searchError = null;
      })
      .addCase(searchChannels.fulfilled, (state, action) => {
        state.isSearching = false;
        state.searchResults = action.payload.channels;
        state.searchQuery = action.payload.query;
        state.hasSearched = true;
        state.searchError = null;
      })
      .addCase(searchChannels.rejected, (state, action) => {
        state.isSearching = false;
        state.searchError = action.error.message || 'Failed to search channels';
        state.hasSearched = true;
      });

    builder
      .addCase(getFollowedChannels.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getFollowedChannels.fulfilled, (state, action) => {
        state.isLoading = false;
        state.followedChannels = action.payload;
        state.channels = action.payload;
        state.error = null;
      })
      .addCase(getFollowedChannels.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to get followed channels';
      });

    builder
      .addCase(followChannel.pending, (state) => {
        state.isFollowing = true;
        state.followError = null;
      })
      .addCase(followChannel.fulfilled, (state, action) => {
        state.isFollowing = false;
        const channel = action.payload;
        if (!state.followedChannels.find(c => c.channelId === channel.channelId)) {
          state.followedChannels.push(channel);
          state.channels.push(channel);
        }
        state.followError = null;
      })
      .addCase(followChannel.rejected, (state, action) => {
        state.isFollowing = false;
        state.followError = action.payload as string || 'Failed to follow channel';
      });

    builder
      .addCase(unfollowChannel.pending, (state) => {
        state.isUnfollowing = true;
        state.followError = null;
      })
      .addCase(unfollowChannel.fulfilled, (state, action) => {
        state.isUnfollowing = false;
        const channelId = action.payload;
        state.followedChannels = state.followedChannels.filter(
          channel => channel.channelId !== channelId
        );
        state.channels = state.channels.filter(
          channel => channel.channelId !== channelId
        );
        if (state.selectedChannel?.channelId === channelId) {
          state.selectedChannel = null;
        }
        state.followError = null;
      })
      .addCase(unfollowChannel.rejected, (state, action) => {
        state.isUnfollowing = false;
        state.followError = action.payload as string || 'Failed to unfollow channel';
      });

    builder
      .addCase(getChannelMetadata.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getChannelMetadata.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedChannel = action.payload;
        state.error = null;
      })
      .addCase(getChannelMetadata.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to get channel metadata';
      });

    builder
      .addCase(triggerManualImport.pending, (state) => {
        state.isImporting = true;
        state.error = null;
      })
      .addCase(triggerManualImport.fulfilled, (state, action) => {
        state.isImporting = false;
        const channelId = action.payload;
        const channel = state.followedChannels.find(c => c.channelId === channelId);
        if (channel) {
          channel.lastCheckedAt = new Date();
        }
        if (state.selectedChannel?.channelId === channelId) {
          state.selectedChannel.lastCheckedAt = new Date();
        }
        state.error = null;
      })
      .addCase(triggerManualImport.rejected, (state, action) => {
        state.isImporting = false;
        state.error = action.payload as string || 'Failed to trigger import';
      });
  },
});

export const {
  clearSearchResults,
  setSearchQuery,
  clearErrors,
  selectChannel,
  resetChannelsState,
} = channelsSlice.actions;

export const selectChannelsState = (state: { channels: ChannelsState }) => state.channels;
export const selectAllChannels = (state: { channels: ChannelsState }) => state.channels.channels;
export const selectFollowedChannels = (state: { channels: ChannelsState }) => state.channels.followedChannels;
export const selectSearchResults = (state: { channels: ChannelsState }) => state.channels.searchResults;
export const selectSelectedChannel = (state: { channels: ChannelsState }) => state.channels.selectedChannel;
export const selectIsChannelsLoading = (state: { channels: ChannelsState }) => state.channels.isLoading;
export const selectIsSearching = (state: { channels: ChannelsState }) => state.channels.isSearching;
export const selectChannelsError = (state: { channels: ChannelsState }) => state.channels.error;
export const selectSearchError = (state: { channels: ChannelsState }) => state.channels.searchError;

export const selectIsChannelFollowed = (channelId: string) => 
  (state: { channels: ChannelsState }) => 
    state.channels.followedChannels.some(channel => channel.channelId === channelId);

export default channelsSlice.reducer;
